<template>
    <transition name="fade">
        <div v-if="isOpen" class="stripe-modal">
            <div class="modal-content relative" v-on-clickaway="closeModal">
                <InnerLoader v-if="isLoading" class="payment-loading" :z-index="999" />
                <div class="modal-header" style="border-bottom: none">
                    <div class="text-right">
                        <i
                            @click="closeModal"
                            class="fas text-grey fa-times text-xl shadow-text-none cursor-pointer"
                        ></i>
                    </div>
                </div>
                <div class="modal-body text-center">
                    <img src="@/assets/stripe-logo-slate.png" class="stripe-logo" />
                    <h3 v-if="title" class="stripe-title mt-0 shadow-text-none">{{ title }}</h3>
                    <div class="flex justify-center items-end">
                        <h4 class="text-xl my-0 mr-1 shadow-text-none">${{ amount }}</h4>
                        <span v-if="unit" class="text-lg shadow-text-none">/ {{ unit }}</span>
                    </div>
                    <stripe-element-card
                        class="pt-6"
                        ref="elementsRef"
                        :pk="stripePublicKey"
                        :hidePostalCode="true"
                        :amount="amount * 100"
                    />
                    <button class="btn w-full stripe-open-button" @click="submit">
                        {{ translate('stripe_modal_button_pay', { amount: amount }) }}
                    </button>
                </div>
            </div>
        </div>
    </transition>
</template>

<script>
import InnerLoader from '@/components/InnerLoader'
import { StripeElementCard } from '@vue-stripe/vue-stripe'
import { load } from 'recaptcha-v3'
import { directive as onClickaway } from 'vue-clickaway'
import UserService from '@/lib/user.js'
import Request from '@/lib/request.js'
import appConfig from '@/app.config.js'
import helperMixins from '@/mixins/helper.js'

export default {
    components: {
        InnerLoader,
        StripeElementCard
    },

    mixins: [helperMixins],

    directives: {
        onClickaway: onClickaway
    },

    props: {
        open: {
            type: Boolean,
            default: false
        },

        title: {
            type: String,
            default: ''
        },

        perUnit: {
            type: String,
            default: ''
        },

        paymentDescription: {
            type: String,
            default: ''
        },

        amount: {
            default: 0
        },

        campaignId: {
            type: String,
            default: null
        },

        couponCode: {
            type: String,
            default: null
        }
    },

    watch: {
        open: function (val) {
            this.isOpen = val
        }
    },

    computed: {
        unit() {
            return this.perUnit
        }
    },

    data() {
        return {
            stripePublicKey: process.env.VUE_APP_STRIPE_PUBLIC_KEY,
            captchaKey: process.env.VUE_APP_RECAPTCHA_SITE_KEY,
            isOpen: false,
            isLoading: false
        }
    },

    methods: {
        submit() {
            this.isLoading = true
            load(this.captchaKey, {
                useRecaptchaNet: true,
                autoHideBadge: true
            })
                .then(async recaptcha => {
                    var token = await recaptcha.execute('validate_captcha', { action: 'validate_captcha' })
                    this.validateStripeCaptcha({ captcha_reply: token }).then(res => {
                        if (res.data.status_code == 200 && res.data.data.validate_result) {
                            this.$refs.elementsRef.stripe
                                .createPaymentMethod('card', this.$refs.elementsRef.element)
                                .then(res => {
                                    if (!res.error) {
                                        var payload = {
                                            payment_method_id: res.paymentMethod.id,
                                            amount: this.amount * 100,
                                            description: this.paymentDescription,
                                            campaign_id: this.campaignId,
                                            coupon_code: this.couponCode
                                        }
                                        this.createPaymentIntent(payload).then(res => {
                                            var data = res.data
                                            if (data.status_code == 200) {
                                                this.confirmCardPayment(data.data)
                                            } else {
                                                this.isLoading = false
                                                this.$emit('result', {
                                                    status_code: 400,
                                                    message: this.translate('stripe_modal_alert_fail_intent')
                                                })
                                            }
                                        })
                                    } else {
                                        this.isLoading = false
                                        this.$emit('result', {
                                            status_code: 400,
                                            message: this.translate('stripe_modal_alert_fail_method')
                                        })
                                    }
                                })
                        } else {
                            this.isLoading = false
                        }
                    })
                })
                .catch(() => {})
        },

        confirmCardPayment(getIntent) {
            var intent = getIntent
            this.$refs.elementsRef.stripe
                .confirmCardPayment(
                    intent.client_secret,
                    { payment_method: { card: this.$refs.elementsRef.element } },
                    { handelAtion: true }
                )
                .then(res => {
                    this.isLoading = false
                    if (res.error) {
                        var payload = {
                            id: intent.id
                        }
                        this.cancelPaymentIntent(payload).then(() => {
                            this.$emit('result', {
                                status_code: 400,
                                message: 'Error: Failed to confirm payment'
                            })
                        })
                    } else {
                        var result = {
                            status_code: 200,
                            data: res.paymentIntent,
                            campaign_id: intent.metadata.campaign_id
                        }

                        result.data.method = 'stripe-checkout'

                        this.$emit('result', result)
                    }
                })
        },

        validateStripeCaptcha(data) {
            const userService = new UserService()
            const token = userService.getToken()
            const request = new Request({
                headers: {
                    Authorization: `Bearer ${token}`
                }
            })

            const url = appConfig.api.validateCaptcha

            return new Promise(function (resolve, reject) {
                request
                    .post(url, data)
                    .then(res => resolve(res))
                    .catch(err => reject(err))
            })
        },

        createPaymentIntent(data) {
            const userService = new UserService()
            const token = userService.getToken()
            const request = new Request({
                headers: {
                    Authorization: `Bearer ${token}`
                }
            })

            const url = appConfig.api.createPaymentIntent

            return new Promise(function (resolve, reject) {
                request
                    .post(url, data)
                    .then(res => resolve(res))
                    .catch(err => reject(err))
            })
        },

        cancelPaymentIntent() {
            const userService = new UserService()
            const token = userService.getToken()
            const request = new Request({
                headers: {
                    Authorization: `Bearer ${token}`
                }
            })

            const url = appConfig.api.removePaymentIntent

            return new Promise(function (resolve, reject) {
                request
                    .get(url)
                    .then(res => resolve(res))
                    .catch(err => reject(err))
            })
        },

        closeModal() {
            if (this.isLoading == true) {
                return
            }

            this.$emit('close')
        }
    }
}
</script>

<style lang="scss">
.stripe-modal {
    font-family: 'Open Sans', sans-serif !important;
    background-color: rgba(0, 0, 0, 0.2);
    width: 100%;
    height: 100%;
    position: fixed;
    top: 0;
    left: 0;
    display: flex;
    justify-content: center;
    align-items: start;
    font-size: 1.2em;
    z-index: 9999;

    .modal-content {
        background-color: #fff;
        margin-top: 4rem;
        padding: 1rem 1.5rem;
        width: 500px !important;
        overflow: hidden;
        border-radius: 3px;
        box-shadow: 2px 2px 20px rgba(0, 0, 0, 0.6);

        @media screen and (max-width: 500px) {
            width: 90% !important;
            margin: 1rem 0.5rem;
        }

        .modal-body {
            margin: 0 0 1rem 0;

            @media screen and (max-width: 500px) {
                margin: 0 0 1rem 0;
            }
        }
    }

    .payment-loading {
        margin: -1rem 0 0 -1.5rem;
    }
}

#card-errors {
    padding: 0.4em 0 0 0.1em;
    font-size: 0.6em;
    color: red;
}

#card-element {
    -webkit-box-shadow: 1px 1px 3px rgba(0, 0, 0, 0.3);
    box-shadow: 1px 1px 3px rgba(0, 0, 0, 0.3);
}

.stripe-title {
    font-size: 1.4rem;
    margin-bottom: 1rem;
    color: #32325d;
}

.stripe-open-button {
    width: 100%;
    font-family: 'Open Sans', sans-serif;
    font-size: 0.8em;
    background-color: #33325d !important;
    padding: 0.8rem 0;
    margin-top: 3rem;
}

.stripe-logo {
    width: 37%;
    margin-top: 0.5em;
    margin-bottom: 0.75em;
}
</style>
